/* @flow strict */

import * as React from 'react';
import _ from 'lodash';
import { DefaultContainerContext } from '/client/src/contexts/defaultContainer';

import { GET } from '/client/src/api';
import CssPinner from '/client/src/components/csspinner';
import Loader from '/client/src/components/Loader';
import CheckInEventListItem from './checkInEventListItem';

import './styles.scss';

type PropsType = {
  handleEventData: (eventId: string | number, eventScheduleId: number | string) => void
};

const CheckInEventList = ({ handleEventData }: PropsType): React.Element<typeof React.Fragment> => {
  const _context = React.useContext(DefaultContainerContext);
  const profileId = _context.profileCurrent.id;
  const PaginatorCounter = 23;

  const [lazyCountSkip, setLazyCountSkip] = React.useState(0);
  const [lazyCountMax, setLazyCountMax] = React.useState(PaginatorCounter);
  const [isPendingUpdate, setIsPendingUpdate] = React.useState(false);
  const [transactionsList, setTransactionsList] = React.useState([]);
  const wrapper = React.useRef();
  const [eventList, setEventList] = React.useState([]);
  const [searchInput, setSearchInput] = React.useState('');

  const URLS = {
    transaction: '/api/v1/1/statistics/events/transaction',
    events: `/api/v1/1/merchants/${profileId}/events?skip_count=${0}&max_result_count=${PaginatorCounter}&show_collaborative_events=true${!!_.size(searchInput) ? `&event_name=${searchInput}` : ''}`
  };

  const handleLoaded = React.useCallback(
    async (error: Error, response: any) => {
      if (!error) {
        await setAllData(response.data.result.items);
      }
    },
    []
  );

  const setAllData = async (response: {}) => {
    try {
      const items = response;

      setEventList(items);
      const _ids = _.map(items, (i: {}): string | number => i.id).join(',');

      if (_.size(_ids)) {
        try {
          const { result } = await GET(`${URLS.transaction}?ids=${_ids}`);

          setTransactionsList([...transactionsList, ...result]);
        } catch (e) {
          console.error('cant get transactions list');
        }
      }
    } catch (e) {
      console.error('cant get events list');
    }
  };

  const updateRequest = async () => {
    setIsPendingUpdate(true);
    setLazyCountSkip(lazyCountSkip + PaginatorCounter);
    setLazyCountMax(lazyCountMax + PaginatorCounter);

    // eslint-disable-next-line
    URLS.events = `/api/v1/1/merchants/${profileId}/events?skip_count=${lazyCountSkip + PaginatorCounter}&max_result_count=${lazyCountMax + PaginatorCounter}&show_collaborative_events=true${!!_.size(searchInput) ? `&event_name=${searchInput}` : ''}`;

    const events = await GET(`${URLS.events}`);

    setEventList([
      ...eventList,
      ...events.result.items
    ]);
    const _ids = _.map(events.result.items, (i: {}): string | number => i.id).join(',');

    if (_.size(_ids)) {
      try {
        const transaction = await GET(`${URLS.transaction}?ids=${_ids}`);

        setTransactionsList([
          ...transactionsList,
          ...transaction.result
        ]);
      } catch (e) {
        console.error('cant get transactions list');
      }
    }
    setIsPendingUpdate(false);
  };

  const handleSearchInput = (searchInput: string) => {
    setLazyCountSkip(0);
    setLazyCountMax(PaginatorCounter);
    setSearchInput(searchInput);
  };

  const handleSearch = async () => {
    setIsPendingUpdate(true);

    // eslint-disable-next-line
    URLS.events = `/api/v1/1/merchants/${profileId}/events?skip_count=${lazyCountSkip}&max_result_count=${lazyCountMax}&show_collaborative_events=true${!!_.size(searchInput) ? `&event_name=${searchInput}` : ''}`;

    const events = await GET(`${URLS.events}`);

    setEventList(events.result.items);
    const _ids = _.map(events.result.items, (i: {}): string | number => i.id).join(',');

    if (_.size(_ids)) {
      try {
        const transaction = await GET(`${URLS.transaction}?ids=${_ids}`);

        setTransactionsList(transaction.result);
      } catch (e) {
        console.error('cant get transactions list');
      }
    }
    setIsPendingUpdate(false);
  };

  React.useEffect(() => {
    handleSearch();
  }, [searchInput]);

  const handleLazyLoad = () => {
    window.onscroll = async () => {
      if (!!searchInput.length) return;

      const lastEl = document.querySelector('.event-list ._row:last-child') || document.querySelector('.event-list .row:last-child');
      const lastElCoords = !_.isNull(lastEl) ? lastEl.getBoundingClientRect() : null;

      if (!_.isNull(lastElCoords) && lastElCoords.bottom < window.innerHeight && !isPendingUpdate) {
        await updateRequest();
      }
    };
  };

  handleLazyLoad();

  return (
    <React.Fragment>
      <div className="sparxo-router-content">
        <div className='sparxo-page-container EventPage' ref={wrapper}>
          <div className='check-in-filter-container'>
            <input
              type="text"
              placeholder='Filter by event name'
              value={searchInput}
              onChange={(e: SyntheticEvent<HTMLInputElement>) => {
                handleSearchInput(e.target.value);
              }}
              autoComplete='off'
            />
            </div>
          <section className='event-list'>
            <Loader
              url={`${URLS.events}`}
              onLoaded={handleLoaded}
            >
              {_.map(eventList, (ev: {}): React.Element => {

                const transactions = {
                  ..._.find(transactionsList, (ti: {}): {} => (_.toString(ti.event_id) === _.toString(ev.id)))
                };

                return (
                  <CheckInEventListItem
                    {...ev}
                    transactions={transactions}
                    handleEventData={handleEventData}
                  />
                );
              })
              }
            </Loader>
          </section>
        </div>
      </div>
    </React.Fragment>
  );
};

export default CheckInEventList;
